/**
 * Dependency-free audio player with a decorative waveform.
 *
 * Renders inside `.staff-list--item__audio`. Plays a real audio file read from
 * `data-src`; the bars are decorative (deterministic heights, not real audio
 * peaks) and fill left-to-right to reflect playback progress. The timer counts
 * DOWN (remaining time).
 *
 * Markup expected per instance:
 *   <div class="staff-list--item__audio" data-src="...">
 *     <button class="staff-list--item__audio-toggle" aria-label="Play"></button>
 *     <div class="staff-list--item__audio-wave"></div>
 *     <span class="staff-list--item__audio-time">0:00</span>
 *   </div>
 */

const BAR_COUNT = 48;
const PLAY_LABEL = 'Play';
const PAUSE_LABEL = 'Pause';

// Shared ellipse (cyan circle) + swappable inner glyph (white triangle / two bars).
const ICON_ELLIPSE = '<circle cx="12" cy="12" r="12" fill="#14BEF0"></circle>';
const ICON_PLAY_GLYPH =
  '<path d="M9.5 7.5 L17 12 L9.5 16.5 Z" fill="#fff"></path>';
const ICON_PAUSE_GLYPH =
  '<rect x="8.5" y="7.5" width="2.5" height="9" fill="#fff"></rect>' +
  '<rect x="13" y="7.5" width="2.5" height="9" fill="#fff"></rect>';

const iconSvg = (glyph) =>
  `<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">${ICON_ELLIPSE}${glyph}</svg>`;

// Track every initialised instance so one can pause the others when it starts.
const instances = [];

/**
 * Deterministic bar height (0..1) for bar `i`. Mimics a voice-memo waveform:
 * mostly short, jagged bars with occasional taller spikes — not smooth hills.
 * Identical on every render (no randomness, no real peaks).
 */
const barHeight = (i) => {
  // Deterministic pseudo-noise in [0, 1): fract of a large-multiplier sine.
  const noise = (n) => {
    const v = Math.sin(n * 12.9898) * 43758.5453;
    return v - Math.floor(v);
  };
  // Per-bar jitter so neighbours alternate sharply (the spiky tall/short look).
  const jitter = noise(i) * 0.55 + noise(i * 3.7 + 2) * 0.45;
  // Slow envelope so bars cluster into gentle louder/quieter groups along the
  // strip rather than being uniform noise.
  const envelope = 0.7 + 0.3 * Math.sin(i * 0.5 + 0.6);
  // Wide range so bars read as distinct vertical lines: short nubs up to full.
  return 0.2 + jitter * envelope * 0.8;
};

const formatTime = (seconds) => {
  if (!isFinite(seconds) || seconds < 0) seconds = 0;
  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

class AudioWaveformPlayer {
  constructor(root) {
    this.root = root;
    this.src = root.getAttribute('data-src');
    this.toggle = root.querySelector('.staff-list--item__audio-toggle');
    this.wave = root.querySelector('.staff-list--item__audio-wave');
    this.timeEl = root.querySelector('.staff-list--item__audio-time');

    this.audio = new Audio();
    this.audio.preload = 'metadata';
    this.audio.src = this.src;

    // Hover overlay context (optional — the player also works standalone).
    // The overlay is a sibling of the player inside `__media`, not an ancestor.
    this.media = root.closest('.staff-list--item__media');
    this.overlay = this.media
      ? this.media.querySelector('.staff-list--item__overlay')
      : null;

    this.bars = [];

    this.renderBars();
    this.setIcon(false);
    this.bindEvents();
    this.bindOverlay();

    // Show full duration once metadata loads; until then leave the markup's
    // initial 0:00 in place.
    if (this.timeEl) this.timeEl.textContent = formatTime(0);
  }

  renderBars() {
    if (!this.wave) return;
    const frag = document.createDocumentFragment();
    for (let i = 0; i < BAR_COUNT; i += 1) {
      const bar = document.createElement('span');
      bar.className = 'staff-list--item__audio-bar';
      bar.style.height = `${Math.round(barHeight(i) * 100)}%`;
      frag.appendChild(bar);
      this.bars.push(bar);
    }
    this.wave.appendChild(frag);
  }

  setIcon(isPlaying) {
    if (!this.toggle) return;
    this.toggle.innerHTML = iconSvg(
      isPlaying ? ICON_PAUSE_GLYPH : ICON_PLAY_GLYPH
    );
    this.toggle.setAttribute(
      'aria-label',
      isPlaying ? PAUSE_LABEL : PLAY_LABEL
    );
  }

  bindEvents() {
    if (this.toggle) {
      this.toggle.addEventListener('click', (e) => {
        // The player lives inside an <a>; don't follow the link on toggle.
        e.preventDefault();
        e.stopPropagation();
        this.togglePlay();
      });
    }

    if (this.wave) {
      this.wave.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.seekFromEvent(e);
      });
    }

    this.audio.addEventListener('loadedmetadata', () => this.updateTime());
    this.audio.addEventListener('timeupdate', () => {
      this.updateProgress();
      this.updateTime();
    });
    this.audio.addEventListener('play', () => this.setIcon(true));
    this.audio.addEventListener('pause', () => this.setIcon(false));
    this.audio.addEventListener('ended', () => {
      this.audio.currentTime = 0;
      this.updateProgress();
      this.updateTime();
      this.setIcon(false);
    });
  }

  bindOverlay() {
    if (!this.overlay || !this.media) return;

    const closeBtn = this.overlay.querySelector(
      '.staff-list--item__overlay-close'
    );
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeOverlay();
      });
    }

    // The close button hides the overlay even while hovered; re-entering the
    // image (a fresh mouseenter) clears that dismissed state so hover works again.
    this.media.addEventListener('mouseenter', () => {
      this.overlay.classList.remove('is-dismissed');
    });

    // Esc closes the overlay when focus is inside it.
    this.overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeOverlay();
    });
  }

  closeOverlay() {
    this.pause();
    if (this.overlay) {
      this.overlay.classList.remove('is-open');
      this.overlay.classList.add('is-dismissed');
    }
  }

  togglePlay() {
    if (this.audio.paused) {
      this.pauseOthers();
      // play() rejects on autoplay restrictions; user gesture here keeps it safe.
      const result = this.audio.play();
      if (result && typeof result.catch === 'function') {
        result.catch(() => this.setIcon(false));
      }
    } else {
      this.audio.pause();
    }
  }

  pause() {
    if (!this.audio.paused) this.audio.pause();
  }

  pauseOthers() {
    instances.forEach((other) => {
      if (other !== this) other.pause();
    });
  }

  seekFromEvent(e) {
    const duration = this.audio.duration;
    if (!isFinite(duration) || duration <= 0) return;
    const rect = this.wave.getBoundingClientRect();
    const ratio = Math.min(
      Math.max((e.clientX - rect.left) / rect.width, 0),
      1
    );
    this.audio.currentTime = ratio * duration;
    this.updateProgress();
    this.updateTime();
  }

  updateProgress() {
    const duration = this.audio.duration;
    const ratio =
      isFinite(duration) && duration > 0
        ? this.audio.currentTime / duration
        : 0;
    const filled = Math.round(ratio * this.bars.length);
    this.bars.forEach((bar, i) => {
      bar.classList.toggle('is-played', i < filled);
    });
  }

  updateTime() {
    if (!this.timeEl) return;
    const duration = this.audio.duration;
    // Count down: show remaining time once duration is known, else full/0:00.
    const remaining =
      isFinite(duration) && duration > 0
        ? duration - this.audio.currentTime
        : 0;
    this.timeEl.textContent = formatTime(remaining);
  }
}

/**
 * Initialise every `.staff-list--item__audio` on the page. Instances with a
 * missing/empty `data-src` are skipped. Safe to call more than once — already
 * initialised roots are flagged and ignored.
 */
export function initAudioWaveform(scope = document) {
  const roots = scope.querySelectorAll('.staff-list--item__audio');
  roots.forEach((root) => {
    if (root.dataset.audioInit === 'true') return;
    const src = root.getAttribute('data-src');
    if (!src || !src.trim()) return; // skip gracefully
    root.dataset.audioInit = 'true';
    instances.push(new AudioWaveformPlayer(root));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAudioWaveform());
} else {
  initAudioWaveform();
}

export default initAudioWaveform;
